<script setup lang="ts">
import { Loader } from '@bluebrick/saas-ui'
import { storeToRefs } from 'pinia'
import { ref, onBeforeMount, watch } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import { useApi } from '@/features/api/useAPI'
import { useAppStore } from '@/features/app/useAppStore'
import { useDataHealthStore } from '@/features/dataHealth/useDataHealthStore'
import useIFrameIncomingMsg from '@/features/iframe/useIFrameIncomingMsg'
import { emitToParent } from '@/features/iframe/useParentEmit'
import { type TelemetryTransaction } from '@/features/telemetry/types'
import { useTelemetry } from '@/features/telemetry/useTelemetry'
import { useUserStore } from '@/features/user/useUserStore'
import { PATH } from '@/routes'

const { DEV, VITE_APP_VERSION_LOCAL_DEV, VITE_JWT_TOKEN_LOCAL_DEV } = import.meta.env

const { updateLastScrapeDates, updatePortalsStatus } = useDataHealthStore()
const { isEmbedded, isIdle } = storeToRefs(useAppStore())

const isReady = ref<boolean>(false)
const router = useRouter()
const route = useRoute()
const { isUserInitialized } = storeToRefs(useUserStore())
const { setUserToken } = useUserStore()
const { updateAppVersionHeader } = useApi()
const { initUser } = useUserStore()
const { startTransaction, trackEvent, trackException } = useTelemetry()

useIFrameIncomingMsg({
	TOKEN_HAS_CHANGED: async (payload) => {
		trackEvent('app', 'Received new token', { isUserInitialized: isUserInitialized.value })
		setUserToken(payload)

		if (!isUserInitialized.value) {
			await initUser()

			trackEvent('app', 'Requesting new scrapers status')
			emitToParent('GET_SCRAPERS_STATUS')
		}
	},
	SCRAPERS_STATUS: (payload) => {
		trackEvent('app', 'Received new scrapers status')

		updatePortalsStatus(payload)

		isReady.value = true
	},
	LAST_SCRAPE_DATES_CHANGED: (payload) => {
		trackEvent('app', 'Received new last scrape dates', {
			data: JSON.stringify(payload)
		})

		updateLastScrapeDates(payload)
	},
	IDLE_STATE_CHANGED: (payload) => {
		trackEvent('app', 'App idle state changed', {
			data: JSON.stringify(payload)
		})

		isIdle.value = payload
	}
})

async function logInWithTokenAndVersion(token: string, version: string | undefined, transaction: TelemetryTransaction | undefined) {
	setUserToken(token)
	updateAppVersionHeader(version)

	const spanUser = transaction?.startSpan('initUser', 'initialization')

	await initUser(transaction)

	spanUser?.end()

	isReady.value = true
}

onBeforeMount(async () => {
	trackEvent('app', 'Preload before mount')

	const transaction = startTransaction('Preload', 'initialization')

	try {
		const spanRouter = transaction?.startSpan('prepareRouter', 'initialization')

		await router.isReady()

		spanRouter?.end()

		isEmbedded.value = !!route.query.embedded || !!route.redirectedFrom?.query.embedded

		if (DEV) {
			if (isEmbedded.value) {
				trackEvent('app', 'Requesting new token - dev')
				emitToParent('GET_TOKEN')
			} else if (VITE_JWT_TOKEN_LOCAL_DEV) {
				logInWithTokenAndVersion(VITE_JWT_TOKEN_LOCAL_DEV, VITE_APP_VERSION_LOCAL_DEV, transaction)
			} else if (route.query.token) {
				trackEvent('app', 'Logging in with token from query - dev')

				logInWithTokenAndVersion(String(route.query.token), '99.99.99', transaction)
			}
		} else {
			if (isEmbedded.value) {
				trackEvent('app', 'Requesting new token - prod - embedded')
				emitToParent('GET_TOKEN')
			} else if (route.query.token) {
				trackEvent('app', 'Logging in with token from query')

				logInWithTokenAndVersion(String(route.query.token), '99.99.99', transaction)
			} else {
				trackEvent('app', 'Requesting login page - prod - not embedded')
				isReady.value = true
			}
		}
	} catch (error) {
		trackException(error, 'Preload beforeMount error')
	} finally {
		transaction?.end()
	}
})

watch(isReady, () => {
	trackEvent('app', 'Preload isReady', { isUserInitialized: isUserInitialized.value, isPreloadReady: isReady.value })

	if (isUserInitialized.value) {
		router.push({
			path: PATH.PLANNER
		})
	} else {
		router.push({
			path: PATH.LOGIN
		})
	}
})
</script>

<template>
	<div class="flex h-screen items-center justify-center">
		<Loader />
	</div>
</template>
